import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import ScreenShareView from "../components/ScreenShareView";
import ShellWatchView from "../components/ShellWatchView";

/* Watch a console session somebody invited you to.
 *
 * Screen-share reuses ScreenShareView in observe mode — the frame decoding
 * and canvas handling are the fiddly parts and two copies drift. A shell
 * needs an xterm mount instead, and it is a SEPARATE component rather than
 * DeviceShell with a flag: that page's job is to create a session, send
 * resizes and forward keystrokes, and a watcher must do none of those.
 * Stripping them out by prop would leave the input path present and one
 * boolean away from live.
 *
 * There is no device id in the URL on purpose: a watcher is not opening a
 * session against a device, they are attaching to an existing one, and the
 * server resolves the device from the session token. Passing a device id
 * would invite somebody to change it.
 */
export default function ConsoleWatchPage() {
  const { kind, token } = useParams<{ kind: string; token: string }>();
  const navigate = useNavigate();

  if (!token || (kind !== "screenshare" && kind !== "shell")) {
    // The kind is a path segment, so it is whatever was typed. The server
    // rejects an unknown one too; this is so the page says something useful
    // instead of opening a socket that will be closed.
    return (
      <div className="p-8 text-center">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          That is not a session you can watch
        </h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Watching works for remote-shell and screen-share sessions. Check the
          link you were sent.
        </p>
        <button
          onClick={() => navigate("/devices")}
          className="mt-4 text-sm px-4 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700"
        >
          Back to devices
        </button>
      </div>
    );
  }

  if (kind === "shell") {
    return (
      <ShellWatchView
        token={token}
        label="the shared shell session"
        onClose={() => navigate(-1)}
      />
    );
  }

  return (
    <ScreenShareView
      deviceId={0}
      hostname="the shared session"
      observeToken={token}
      onClose={() => navigate(-1)}
    />
  );
}
